/**
 * Created by Arif Topcu in 2019
 */

import Checkbox from "@ls/react-checkbox";
import produce from "immer";
import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Combobox from "@ls/react-combobox-grid";
import CalcType, { calcTypeOptions } from "state/Constants/CalcType";
import ScaleType, { scaleTypeOptions } from "state/Constants/ScaleType";
import { useRatingOptionsQuery } from "state/ratings/hooks";
import { Grid, Cell } from "styled-css-grid";
import styled from "styled-components";

const StyledCell = styled(Cell)`
  display: inline-flex;
  flex-flow: column;
  justify-content: flex-end;
`;
const columnDefs = [
  {
    headerName: "Rating",
    field: "id",
  },
  {
    headerName: "Provider",
    field: "provider",
    width: 90,
    suppressSizeToFit: true,
  },
];

const RatingsSelectorComplication = ({ setDefinition, definition }) => {
  const { isFetching, data } = useRatingOptionsQuery();
  const ratingOptions = data?.ratingOptions ?? null;
  const ratingOptionsByRatingId = data?.ratingOptionsByRatingId ?? null;

  const [
    onChangeRatingId,
    onChangeScaleType,
    onChangeCalcType,
    onChangeIsText,
    onChangeIsNumeric,
  ] = useMemo(() => {
    return [
      (ratingId) =>
        setDefinition(
          produce(definition, (draft) => {
            draft.ratingId = ratingId;
            if (ratingId) {
              const rating = ratingOptionsByRatingId?.[ratingId];
              draft.analytic = rating?.analytic ?? null; //rating could be null because of 1) rating objects havent yet been fetched or user cleared the selection drop down and ratingId is null
            } else {
              draft.analytic = null;
            }
          }),
        ),
      (value) =>
        setDefinition(
          produce(definition, (draft) => {
            draft.scaleType = value;
            if (value === ScaleType.Ratio) {
              draft.calcType = CalcType.Relative;
            }
          }),
        ),
      (value) =>
        setDefinition(
          produce(definition, (draft) => {
            draft.calcType = value;
          }),
        ),
      (value) =>
        setDefinition(
          produce(definition, (draft) => {
            draft.isText = value;
          }),
        ),
      (value) =>
        setDefinition(
          produce(definition, (draft) => {
            draft.isNumeric = value;
          }),
        ),
    ];
  }, [definition, ratingOptionsByRatingId, setDefinition]);

  const isCalcTypeDisabled = definition.scaleType === ScaleType.Ratio;

  return (
    <Grid columns={12} gap="4px">
      <Cell width={6}>
        <Combobox
          options={ratingOptions}
          columnDefs={columnDefs}
          onChange={onChangeRatingId}
          isLoading={isFetching}
          value={definition.ratingId}
          labelKey={"id"}
          valueKey={"id"}
          label="Rating:"
          variation={0}
          size={"S"}
          gridHeight={175}
        />
      </Cell>
      <StyledCell width={3}>
        <Combobox
          options={scaleTypeOptions}
          onChange={onChangeScaleType}
          value={definition.scaleType}
          label="Type:"
          clearable={false}
          variation={0}
          size={"S"}
          gridHeight={120}
        />
      </StyledCell>
      <StyledCell width={3}>
        <Combobox
          options={calcTypeOptions}
          onChange={onChangeCalcType}
          value={definition.calcType}
          label="Calc:"
          clearable={false}
          variation={0}
          size={"S"}
          gridHeight={120}
          disabled={isCalcTypeDisabled}
        />
      </StyledCell>
      <StyledCell width={3}>
        <Checkbox
          label="Text"
          value={definition.isText}
          onChange={onChangeIsText}
        />
      </StyledCell>
      <StyledCell width={2}>
        <Checkbox
          label="Numeric"
          value={definition.isNumeric}
          onChange={onChangeIsNumeric}
        />
      </StyledCell>
    </Grid>
  );
};

RatingsSelectorComplication.propTypes = {
  setDefinition: PropTypes.func.isRequired,
  definition: PropTypes.object.isRequired,
};

export default RatingsSelectorComplication;
